import { useContext, useEffect } from 'react';

import dayjs from 'dayjs';

import { ValidateForm } from '../../../../../../../shared/utils';

import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  InputForm,
  InputNumberForm,
  RowSize,
  SelectForm,
} from '../../../../../../../shared/components';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../contexts';
import { RenewalQuotationClient } from '../../interfaces/load-renewal-quotation-response-interface';

export const AdditionalDataSection = () => {
  const validateForm = new ValidateForm();

  const {
    additionalDataSectionForm,
    additionalDataFormValues,
    setAdditionalDataFormValues,
    branchEconomyActivityList,
    canShowSpinner,
    civilStatusList,
    countryList,
    professionList,
  } = useContext(GenericDataFormContext);

  const { quotationToRenewal, savedQuotation } = useContext(QuotationInfoContext);
  const { additionalDataClientSelected } = useContext(StepOneFormContext);
  const clientQuotation = (quotationToRenewal?.cliente ||
    savedQuotation?.cliente) as RenewalQuotationClient;

  const normalizeSelectValue = (value?: string | number) => {
    if (
      value === undefined ||
      value === null ||
      String(value) === '0' ||
      String(value).toLowerCase() === 'undefined'
    ) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const parseBirthDate = (value?: string) => {
    if (!value || value === '0001-01-01' || value === '1901-01-01') return undefined;
    const parsed = dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY'], true);
    return parsed.isValid() ? parsed : undefined;
  };

  const normalizeNumberValue = (value?: string | number) => {
    if (
      value === undefined ||
      value === null ||
      String(value).toLowerCase() === 'undefined' ||
      String(value).trim() === ''
    ) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  useEffect(() => {
    if (additionalDataFormValues) {
      additionalDataSectionForm.setFieldsValue(additionalDataFormValues);
      return;
    }

    if (additionalDataSectionForm.isFieldsTouched()) return;

    additionalDataSectionForm.setFieldsValue({
      telephoneNumber:
        additionalDataClientSelected?.telefonoParticular ??
        clientQuotation?.telefonoParticular,
      telephoneNumberJob:
        additionalDataClientSelected?.telefonoLaboral ??
        clientQuotation?.telefonoLaboral,
      cellphoneNumber:
        additionalDataClientSelected?.telefonoCelular ??
        clientQuotation?.telefonoCelular,
      webPage:
        additionalDataClientSelected?.paginaWeb ?? clientQuotation?.paginaWeb,
      birthDate: parseBirthDate(
        additionalDataClientSelected?.fechaNacimiento ??
          clientQuotation?.fechaNacimiento
      ),
      placeOfBirth:
        additionalDataClientSelected?.lugarNacimiento ??
        clientQuotation?.lugarNacimiento,
      countryBirth: normalizeSelectValue(
        additionalDataClientSelected?.idPaisNacimiento ??
          clientQuotation?.idPaisNacimiento
      ),
      profession: normalizeSelectValue(
        additionalDataClientSelected?.idProfesion ?? clientQuotation?.idProfesion
      ),
      civilStatus: normalizeSelectValue(
        additionalDataClientSelected?.idEstadoCivil ??
          clientQuotation?.idEstadoCivil
      ),
      cuilNumber: normalizeNumberValue(clientQuotation?.cuil),
      economicActivity: normalizeSelectValue(
        additionalDataClientSelected?.idRamaEconomica ??
          clientQuotation?.idRamaEconomica
      ),
      addressEmail:
        additionalDataClientSelected?.email ?? clientQuotation?.email,
      rutaNumber: normalizeNumberValue(
        additionalDataClientSelected?.numeroRuta ?? clientQuotation?.numeroRuta
      ),
    });
  }, [
    additionalDataSectionForm,
    additionalDataFormValues,
    additionalDataClientSelected,
    clientQuotation,
  ]);

  useEffect(() => {
    return () => {
      setAdditionalDataFormValues(additionalDataSectionForm.getFieldsValue(true));
    };
  }, [additionalDataSectionForm, setAdditionalDataFormValues]);

  return (
    <ContentSection level='h3' title='Datos Adicionales'>
      <Form
        formProps={{
          form: additionalDataSectionForm,
          name: 'additional-data-form',
        }}
      >
        <RowSize>
          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Teléfono Particular:',
                name: 'telephoneNumber',
                rules: validateForm.generateRules({
                  label: 'teléfono particular',
                  rules: ['phone'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Teléfono Laboral:',
                name: 'telephoneNumberJob',
                rules: validateForm.generateRules({
                  label: 'teléfono laboral',
                  rules: ['phone'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Teléfono Celular:',
                name: 'cellphoneNumber',
                rules: validateForm.generateRules({
                  label: 'teléfono celular',
                  rules: ['phone'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Página Web:',
                name: 'webPage',
                rules: validateForm.generateRules({
                  label: 'página web',
                  rules: ['web'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <DatePickerForm
              formProps={{
                label: 'Fecha de Nacimiento:',
                name: 'birthDate',
                rules: validateForm.generateRules({
                  label: 'fecha de nacimiento',
                  rules: ['dateNotAfterToday'],
                }),
              }}
              datePickerProps={{
                maxDate: dayjs().subtract(1, 'day'),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Lugar de Nacimiento:',
                name: 'placeOfBirth',
                rules: validateForm.generateRules({
                  label: 'lugar de nacimiento',
                  rules: ['text'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'País de Nacimiento:',
                name: 'countryBirth',
              }}
              selectProps={{
                disabled: countryList.length < 1,
                loading: canShowSpinner,
                options: countryList,
                showSearch: true,
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'Profesión:',
                name: 'profession',
              }}
              selectProps={{
                disabled: professionList.length < 1,
                loading: canShowSpinner,
                options: professionList,
                showSearch: true,
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'Estado Civil:',
                name: 'civilStatus',
              }}
              selectProps={{
                disabled: civilStatusList.length < 1,
                loading: canShowSpinner,
                options: civilStatusList,
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'CUIL:',
                name: 'cuilNumber',
                rules: validateForm.generateRules({
                  label: 'cuil',
                  rules: ['cuil'],
                }),
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'Actividad Económica',
                name: 'economicActivity',
              }}
              selectProps={{
                disabled: branchEconomyActivityList.length < 1,
                loading: canShowSpinner,
                options: branchEconomyActivityList,
                showSearch: true,
              }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputForm
              formProps={{
                label: 'Correo Electrónico:',
                name: 'addressEmail',
                rules: validateForm.generateRules({
                  label: 'correo electrónico',
                  rules: ['email'],
                }),
              }}
              inputProps={{ type: 'email' }}
            />
          </ColSize>

          <ColSize size={25}>
            <InputNumberForm
              formProps={{
                label: 'R.U.T.A',
                name: 'rutaNumber',
              }}
            />
          </ColSize>
        </RowSize>
      </Form>
    </ContentSection>
  );
};
